import React, { useState, useEffect } from 'react';
import AddButton from '../Buttons/AddButton';
import Skill from './Skill';
import { SkillProps, SkillsManagerProps } from './types';
import strings from '../../resources/strings';
import { autoId } from '../../utils/UidGenerator';
import './SkillsManager.scss';
import AddImage from '../AddImage/AddImage';
import { storage } from '../../resources/storage';
import { FieldTextInput } from '../FieldTextInput';
import { Checkbox, FormControlLabel, TextareaAutosize } from '@mui/material';

const SkillsManager: React.FC<SkillsManagerProps> = (props) => {
    const MAX_DESCRIPTION_LENGTH = 170;
    const [skills, setSkills] = useState<SkillProps[]>([]);
    const [image, setImage] = useState(props.data?.image ?? '');
    const [description, setDescription] = useState(props?.data?.description);
    const [isSurveyDifferent, setIsSurveyDifferent] = useState<boolean>(false);
    useEffect(() => {
        if (props.data) {
            setSkills(
                props.data.skills.map((skill) => {
                    return { skill: skill, selectedLanguage: props.selectedLanguage, languages: props.languages };
                }),
            );
            setIsSurveyDifferent(props.data.isSurveyDifferent);
        }
    }, [props.data]);

    useEffect(() => {
        if (skills.length > 0)
            setSkills(
                skills.map((skill) => {
                    return { ...skill, selectedLanguage: props.selectedLanguage };
                }),
            );
    }, [props.selectedLanguage]);

    const handleAdd = () => {
        const id = autoId();
        const newSkill = {
            skill: {
                id: id,
                name: props.languages.map((lang) => {
                    return { [lang.code]: '' };
                }),
                image: '',
            },
            selectedLanguage: props.selectedLanguage,
            languages: props.languages,
        } as unknown as SkillProps;
        setSkills([newSkill, ...skills]);
    };

    return (
        <div className="skills-manager">
            {props.languages.map((lang) => (
                <div key={lang.code}>
                    <TextareaAutosize
                        key={`description_${lang.code}_ta`}
                        defaultValue={description?.[lang.code]}
                        id={`description_${lang.code}`}
                        placeholder={`Course description (maximum ${MAX_DESCRIPTION_LENGTH} characters)`}
                        name={`description_${lang.code}`}
                        maxRows={6}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        style={{ display: lang.code !== props.selectedLanguage ? 'none' : 'block' }}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            setDescription({ ...description, [lang.code]: e.target.value });
                        }}
                    ></TextareaAutosize>
                    <FieldTextInput
                        key={`description_${lang.code}_tf`}
                        value={description?.[lang.code]}
                        variant="outlined"
                        required={false}
                        type="text"
                        id={`description_${lang.code}`}
                        label="Course description"
                        name={`description_${lang.code}`}
                        hidden={true}
                    ></FieldTextInput>
                </div>
            ))}
            <div className="mission-image">
                <FieldTextInput
                    value={image}
                    hidden={true}
                    variant="outlined"
                    required={false}
                    type="text"
                    id={'image'}
                    label=""
                    name={'image'}
                ></FieldTextInput>
                <AddImage
                    path={storage.missions}
                    showMessage={false}
                    customName={`${autoId()}`}
                    sendOnDrop={true}
                    setGetImgAddress={(url: string) => setImage(url)}
                    actualEventFile={image}
                ></AddImage>
            </div>
            <div className="section">
                <FieldTextInput
                    value={(isSurveyDifferent ?? false).toString()}
                    hidden={true}
                    variant="outlined"
                    required={false}
                    type="boolean"
                    id={'survey'}
                    label=""
                    name={'survey'}
                ></FieldTextInput>
                <FormControlLabel
                    control={
                        <Checkbox
                            id={'survey'}
                            name={'survey'}
                            sx={{ '& .MuiSvgIcon-root': { fontSize: '2.4rem' } }}
                            checked={isSurveyDifferent ?? false}
                            onChange={(event) => {
                                setIsSurveyDifferent(event.target.checked);
                            }}
                        />
                    }
                    label={strings.missions.addMissionDialog.surveyDifferentLabel}
                    sx={{ '.MuiFormControlLabel-label': { fontWeight: 'bold', fontSize: '1.2rem' } }}
                />
            </div>
            <h3>{strings.missions.addMissionDialog.skillsLabel}</h3>
            <AddButton action={handleAdd}></AddButton>
            <div className="skills-manager-container">
                {skills.map((item) => {
                    return <Skill key={`${item.skill.id}`} {...item}></Skill>;
                })}
            </div>
        </div>
    );
};

export default SkillsManager;
